/* eslint-disable max-len */
import { invokeLater } from '../../../CodesThatsGottaGoSomewhereElse';
import { defineCommand } from '../../../concepts/commands/CommandDefinition';
import { component, property } from '../../../concepts/objects'; // Goes up to src, then concepts
import { memoUsing } from '../../../concepts/objects/MemoUsing';
import { Png } from '../../../platform/Png';
import { Point } from '../../../platform/geom/Point';
import { Rect } from '../../../platform/geom/Rect';
import { EditorSession } from '../../canvas/EditorSession';
import { ConvertToPng, ConvertToSvg } from '../../canvas/entities/ConvertTo';
import {
    Figure,
    Physical,
    PotentialInteraction,
    TransformHints,
    TransformScope
} from '../../canvas/entities/Physical'; // Goes up to src/app, then canvas
import { RectangularPhysical } from '../../canvas/entities/RectangularPhysical'; // Goes up to src/app, then canvas
import { ScaleChildren } from '../../canvas/entities/ScaleChildren';
import { ToolboxController } from '../../canvas/tooling/ToolboxController';
import { ActiveSketchInfo, CreateActiveSketchInfo } from './ActiveSketchInfo';
import { SketchLayout } from './SketchLayout';
import { sketchPropertyLayout } from './SketchPropertyLayout';
import { SketchStroke } from './SketchStroke';

const mutation = defineCommand(
    'sketchMutation',
    null,
    (cc, [id]: [string]) => {
        invokeLater(() => {
            const toolbox = cc.resolve(ToolboxController);
            const session = cc.resolve(EditorSession);

            session.update(ActiveSketchInfo, CreateActiveSketchInfo(id));
            toolbox.openWidgetForTool('markerTool');

        });
    },
    [],
);

const sketchDef = {
    name: 'Sketch',
    implements: [Physical, ConvertToPng, ConvertToSvg, ScaleChildren],
    mutation,
    propertyLayout: sketchPropertyLayout,
};


@component(sketchDef)
export class Sketch extends RectangularPhysical implements Physical, ConvertToPng, ConvertToSvg {

    @property({ type: 'number', default: 0 })
    private donk;

    public get boundary() {
        return true;
    }

    @memoUsing('children', 'donk')
    public get layout() {
        return SketchLayout(this);
    }

    public get color() {
        const strokes = this.polarize(SketchStroke);
        if (!strokes.length) return null;
        const first = strokes[0]?.color;
        return strokes.every(x => x.color === first) ? first : null;
    }

    public set color(value: string) {
        const strokes = this.polarize(SketchStroke);
        for (const ss of strokes) {
            ss.color = value;
        }
        this.donk++;
    }

    public get thickness() {
        const strokes = this.polarize(SketchStroke);
        if (!strokes.length) return null;
        const first = strokes[0]?.thickness;
        return strokes.every(x => x.thickness === first) ? first : null;
    }

    public set thickness(value: number) {
        const strokes = this.polarize(SketchStroke);
        for (const ss of strokes) {
            ss.thickness = value;
        }
        this.donk++;
    }

    protected get aggregateStrokeVersion() {
        return this.polarize(SketchStroke).map(x => x.version).join();
    }

    public queryTransformHints(_singular: boolean): TransformHints {
        return {
            ...TransformHints.default,
            scaleX: this.canScale(),
            scaleY: this.canScale(),
            scaleMode: 'fixed',
        };
    }

    public append(newStroke: SketchStroke) {

        const strokes = this.polarize(SketchStroke);
        const figures = strokes.map(x => x.createFigure());

        figures.push(newStroke.createFigure());
        strokes.push(newStroke);

        // If first,

        this.add(newStroke);

        // Refigure the sketch
        this.setFromFigure(
            Rect.fromPoints(figures.flatMap(x => x)).inflate(10).points()
        );

        // Refigure the strokes using their figure from before we updated
        for (let i = 0; i < strokes.length; i++) {
            strokes[i].setFromFigure(figures[i]);
        }
    }

    public truncate(stroke: SketchStroke) {

        const others = this.polarize(SketchStroke).filter(x => x !== stroke);
        const figures = others.map(x => x.createFigure());

        this.remove(stroke.removeSelf());
        if (!others.length) return;

        // Refigure the sketch
        this.setFromFigure(
            Rect.fromPoints(figures.flatMap(x => x)).points()
        );

        // Refigure the strokes using their figure from before we updated
        for (let i = 0; i < others.length; i++) {
            others[i].setFromFigure(figures[i]);
        }
    }

    public applyTransformation(scope: TransformScope, from: Figure, to: Figure): this {
        this.donk++;
        return super.applyTransformation(scope, from, to);
    }

    public test(mpt: Point): boolean {
        const { base, paths } = this.layout;
        const rpt = this.toRelative(mpt);
        if (base.contains(rpt)) {
            for (const { style, size, hit } of paths) {
                if (style === 'fill' && hit.contains(rpt, 5)) {
                    return true;
                }
                if (style === 'stroke' && hit.contains(rpt, size + 5)) {
                    return true;
                }
            }
        }
        return false;
    }

    public testInteraction({ type }: PotentialInteraction): [boolean, string] {
        if (type !== 'invoke') {
            return [true, 'default'];
        }
        return [false, 'none'];
    }

    public toText(): string {
        const { paths } = this.layout;

        const p = 50;

        const attrs = {
            xmlns: 'http://www.w3.org/2000/svg',
            width: this.width + (p * 2),
            height: this.height + (p * 2),
            viewBox: `0 0 ${this.width + (p * 2)} ${this.height + (p * 2)}`,
            fill: 'none',
        };

        const attrStr = Object.entries(attrs).map(([k, v]) => `${k}="${v}"`).join(' ');

        const svg = `<svg ${attrStr}>
            <g transform="translate(${p} ${p})">
                ${paths.map(x => x.style === 'fill'
            ? `<path d="${x.path.toString()}" fill="${x.color}" />`
            : `<path d="${x.path.toString()}" fill="none" stroke-linecap="round" stroke-linejoin="round" stroke="${x.color}" stroke-width="${x.size}" />`
        ).join('\n')}
            </g>
        </svg>`;

        return svg;
    }

    public toSvg() {
        return Promise.resolve(this.toText());
    }

    public async toPng() {
        const svg = await this.toSvg();
        const png = await Png.fromSvg(svg);
        return png;
    }

    protected canScale() {
        return this.polarize(SketchStroke).some(x => x.spine.length > 2);
    }
}
